import { HoverPopover } from '@app/components/base/HoverPopover';
import { LTime } from '@app/components/date/LTime';
import { AuditSchedule } from '@mi-front/core';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { Chip } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { ScheduleMatchStatusDetails } from './ScheduleMatchStatusDetails';

const NO_MATCH_MESSAGE = defineMessage({
  id: 'audit.schedulesListScreen.noMatch',
  defaultMessage: 'Sin match',
});

type Props = {
  schedule: AuditSchedule;
  size?: 'small' | 'medium';
};

/**
 * Whether a mention match falls outside the schedule's before/after tolerance window.
 * Falls back to legacy tolerance fields when `toleranceConfig` is absent.
 */
function isMentionDiffOffTolerance(schedule: AuditSchedule, diffMinutes: number): boolean {
  const before = schedule.toleranceConfig?.before?.value ?? schedule.toleranceBefore;
  const after = schedule.toleranceConfig?.after?.value ?? schedule.tolerance;

  if (diffMinutes < 0) {
    return before != null && Math.abs(diffMinutes) > before;
  }

  return after != null && diffMinutes > after;
}

/**
 * Resolves match datetime and schedule diff in minutes for spot or mention matches.
 */
function getMatchInfo(schedule: AuditSchedule): {
  matchDatetime: string;
  diffMinutes: number | null;
  isOffTolerance: boolean;
} | null {
  const spotMatch = schedule.matches?.[0];
  const mentionMatch = schedule.mentionMatch;

  if (spotMatch) {
    return {
      matchDatetime: spotMatch.datetime,
      diffMinutes: spotMatch.matchedScheduleDiff,
      isOffTolerance: spotMatch.matchedScheduleConfirmationType === 'OFF_TOLERANCE',
    };
  }

  if (mentionMatch) {
    const diffMinutes =
      (new Date(mentionMatch.datetime).valueOf() - new Date(schedule.datetime).valueOf()) / 60000;

    return {
      matchDatetime: mentionMatch.datetime,
      diffMinutes,
      isOffTolerance: isMentionDiffOffTolerance(schedule, diffMinutes),
    };
  }

  return null;
}

/**
 * Schedule match status chip (time + icon). Tolerance and diff live in a hover popover.
 */
export function ScheduleMatchStatus({ schedule, size = 'small' }: Props) {
  const { $t } = useIntl();
  const matchInfo = getMatchInfo(schedule);

  if (!matchInfo) {
    return (
      <HoverPopover
        trigger={<Chip size={size} label={$t(NO_MATCH_MESSAGE)} />}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <ScheduleMatchStatusDetails
          schedule={schedule}
          matchDatetime={null}
          diffMinutes={null}
          isOffTolerance={false}
        />
      </HoverPopover>
    );
  }

  const { matchDatetime, diffMinutes, isOffTolerance } = matchInfo;

  return (
    <HoverPopover
      trigger={
        <Chip
          icon={<AssignmentTurnedInOutlinedIcon />}
          label={<LTime value={matchDatetime} />}
          size={size}
          variant="outlined"
          color={isOffTolerance ? 'warning' : 'success'}
        />
      }
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <ScheduleMatchStatusDetails
        schedule={schedule}
        matchDatetime={matchDatetime}
        diffMinutes={diffMinutes}
        isOffTolerance={isOffTolerance}
      />
    </HoverPopover>
  );
}
